import { Component, OnInit, DoCheck, Input, forwardRef, OnDestroy, Output, EventEmitter } from '@angular/core';
import { TemplateService } from '../../../../../services/template.service';
import { CALENDAR_LOCALE } from '../../../../../services/constant';
import { AbstractControl, AsyncValidatorFn, ControlValueAccessor, FormArray, FormBuilder, FormGroup, NG_ASYNC_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, Validators } from '@angular/forms';
import {combineLatest, concat, from, Observable, of, Subject, Subscription, timer} from 'rxjs';
import { ApiService } from '../../../../../services/api.service';
import { TranslateService } from '@ngx-translate/core';
import { catchError, debounceTime, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../../../../environments/environment';
import { AuthenticationService } from '../../../../../services/authentication.service';
import * as moment from 'moment';

@Component({
  selector: 'app-document-form',
  templateUrl: './document-form.component.html',
  styleUrls: ['./document-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DocumentFormComponent),
      multi: true
    },
    {
      provide: NG_ASYNC_VALIDATORS,
      useExisting: forwardRef(() => DocumentFormComponent),
      multi: true
    },
    TemplateService
  ]
})
export class DocumentFormComponent implements OnInit, DoCheck, OnDestroy, ControlValueAccessor, Validator {
  onChange: any = () => { };
  onTouched: any = () => { };

  @Input() documentType: string;
  @Input() isSubmitted: boolean;
  @Input() isDuplicateDocumentNo: boolean;

  @Output() documentRequestSelected: EventEmitter<any> = new EventEmitter;
  @Output() abbDataSelected: EventEmitter<any> = new EventEmitter();
  @Input() documentInformation: any;
  @Input() docProcessType: any;

  documentForm: FormGroup;
  subscription: Subscription;
  documentTypes: any[];
  documentRequests$: Observable<any>;
  documentRequestInput$ = new Subject<string>();
  searchingDocumentRequest = false;
  purposeError: Boolean = false;

  abbData$: Observable<any>;
  abbNoInput$ = new Subject<string>();
  searchingAbbNo = false;

  abbNoChecked = false;
  confirmationNoChecked = false;
  enabledCustomerRequest = environment.enabledCustomerRequest;

  today = new Date();
  lang: string;

  calendarLocale = CALENDAR_LOCALE.EN;
  minSelectableDate: Date;
  maxSelectableDate: Date;

  departments = [];

  constructor(
    private apiService: ApiService,
    private authService: AuthenticationService,
    private formBuilder: FormBuilder,
    private template: TemplateService,
    private translateService: TranslateService,
  ) { }

  ngDoCheck() {
    this.changeCalendarLocale();
  }

  ngOnInit() {
    this.changeCalendarLocale();
    this.setDocumentTypes();
    this.createDocumentForm();
    this.loadDocumentRequest();
    this.loadAbbData();
    this.setMaxMinDate();
    this.loadDepartments();

    // any time the inner form changes update the parent of any change
    this.createSubscriptions();
    if (this.documentInformation) {
      if (this.documentInformation.header) {
        this.abbNoChecked = this.documentInformation.header.abb_no !== '';
        this.confirmationNoChecked = this.documentInformation.header.confirmation_no !== '';
        this.abbNo.enable();
        this.confirmationNo.enable();
      } else {
        this.abbNoChecked = this.documentInformation.abb_no !== '';
        this.confirmationNoChecked = this.documentInformation.confirmation_no !== '';
        this.abbNo.enable();
        this.confirmationNo.enable();
        this.returnDocumentDropdown(this.documentInformation);
      }
    }
  }

  processPurpose(event): string | null {
    if (event.rd_document_type !== 'receipt_tax_invoice') {
      return null;
    }
    const issueDate = new Date(event.header.issue_dt);
    const issueDateDisplay = moment(issueDate).format('YYYY-MM-DDTHH:mm:ss') + ' GMT +7:00';
    return `เป็นการยกเลิก และออกใบกำกับภาษีฉบับใหม่แทนฉบับเดิมเลขที่ ${event.header.invoice_number} วันที่ ${issueDateDisplay}`
  }

  setMaxMinDate() {
    if (this.isOperation() === true) {
      this.minSelectableDate = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
      this.maxSelectableDate = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
    } else {
      this.minSelectableDate = new Date(2000, 0, 1);
      this.maxSelectableDate = new Date(2030, 11, 31);
    }
  }

  isOperation(): boolean {
    return this.authService.isOperation();
  }

  returnDocumentDropdown(documentInformation: any) {
    this.apiService.getDocumentRequestDropdown({ request_no: documentInformation.request_no }).subscribe( res => {
      this.documentRequests$ = from([res]);
      this.onChangeDocumentRequest(res);
    });

    if (documentInformation.abb_no) {
      this.apiService.getAbbDataDropdown({abb_no: documentInformation.abb_no}).subscribe(res => {
        this.abbData$ = from([res]);
        this.onChangeAbbNo(res[0]);
        this.loadAbbData(res);
      });
    }
  }

  get currentLang(): string {
    return this.translateService.currentLang;
  }

  loadDocumentRequest() {
    this.documentRequests$ = concat(
      of([]),
      this.documentRequestInput$.pipe(
        filter((term) => term != null && term.length >= 2),
        distinctUntilChanged(),
        debounceTime(300),
        tap(() => this.searchingDocumentRequest = true),
        switchMap((term) => {
          return this.apiService.getDocumentRequestDropdown({ request_no: term }).pipe(
            catchError(() => of([])),
            tap(() => this.searchingDocumentRequest = false),
          );
        })
      )
    );
  }

  loadAbbData(existedAbbData?: any[]) {
    this.abbData$ = concat(
      existedAbbData ? of(existedAbbData) : of([]),
      this.abbNoInput$.pipe(
        filter((term) => term != null && term.length >= 2),
        distinctUntilChanged(),
        debounceTime(300),
        tap(() => this.searchingAbbNo = true),
        switchMap((term) => {
          return this.apiService.getAbbDataDropdown({ abb_no: term }).pipe(
            catchError(() => of([])),
            tap(() => this.searchingAbbNo = false),
          );
        })
      )
    );
  }

  loadDepartments(): void {
    this.apiService.getDepartments().subscribe(
      (res: any) => {
        if (res.length > 0) {
          if (this.translateService.currentLang === 'en') {
            this.departments = res.map(
              (department) => ({
                label: department.name_en,
                value: department.id,
              })
            );
          } else {
            this.departments = res.map(
              (department) => ({
                label: department.name_th,
                value: department.id,
              })
            );
          }
        }
      },
      (error: any) => {
        console.log(error);
      }
    );
  }

  changeDepartment(event): void {
    this.getRunnningInvoiceNo(event.value);
    this.documentForm.patchValue({
      department: event.value,
    });
  }

  onChangeDocumentRequest(event) {
    if (event && event.confirmation_no) {
      this.confirmationNoChecked = true;
      this.confirmationNo.enable();
    } else {
      this.confirmationNoChecked = false;
      this.confirmationNo.disable();
    }

    this.documentForm.patchValue({
      confirmationNo: event ? event.confirmation_no : '',
    });

    event && event.length
      ? this.documentRequestSelected.emit(event[0])
      : this.documentRequestSelected.emit(event);
  }

  onChangeAbbNo(event) {
    this.abbDataSelected.emit(event);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  writeValue(value: any) {
    if (value) {
      value.documentNo = null;
      this.documentForm.setValue(value);
      if (this.docProcessType === 'reissue') {
        // unique cases doc type in dex 0 for cn dn is null
        if (!value.referenceDocuments[0].documentType) {
          const required = (this.isCreditNote || this.isDebitNote) ? Validators.required : null;
          const refCnDnList = [];
          for (let i = 0; i < JSON.parse(this.documentInformation.reference.doc_no_out_system.replace(/'/g, '"')).length; i++) {
               refCnDnList.push({
              documentNo: JSON.parse(this.documentInformation.reference.doc_no_out_system.replace(/'/g, '"'))[i],
              documentType: JSON.parse(this.documentInformation.reference.document_type.replace(/'/g, '"'))[i],
              documentDate: new Date(JSON.parse(this.documentInformation.reference.issue_dt.replace(/'/g, '"'))[i])
            });
          }
          refCnDnList.forEach((ref, index) => {
            while (this.referenceDocuments.length <= index) {
              this.referenceDocuments.push(this.formBuilder.group({
                  documentNo: [null, required],
                  documentType: [null, required],
                  documentDate: [null, required]
              }));
            }

            this.referenceDocuments.at(index).setValue(ref);
          });
        }
      }
      this.onChange(value);
      this.onTouched();
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  // communicate the inner form validation to the parent form
  validate(_control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> {
    return new Promise(resolve =>
      this.documentForm.valid ? resolve(null) : resolve({ documentForm: { valid: false } })
    );
  }

  createSubscriptions() {
    // Subscribe to valueChanges and statusChanges
    this.subscription = combineLatest([this.documentForm.valueChanges, this.documentForm.statusChanges]).subscribe(
      () => {
        this.onChange(this.documentForm.getRawValue());
        this.onTouched();
      });
    this.documentForm.get('purpose').valueChanges.subscribe(() => {
        this.documentForm.get('referenceDocuments.0').updateValueAndValidity(); // Trigger validation on purpose change
      });
  }

  setDocumentTypes() {
    this.documentTypes = this.template.getDropdown();

    if (this.isCreditNote || this.isDebitNote) {
      this.documentTypes.shift();
    } else {
      this.documentTypes = this.documentTypes.filter(element => element.value === this.documentType);
    }
  }

  changeCalendarLocale() {
    this.lang = localStorage.getItem('lang');
    this.calendarLocale = this.lang && this.lang === 'th' ? CALENDAR_LOCALE.TH : CALENDAR_LOCALE.EN;
  }

  get isReceiptTaxInvoice() {
    return this.documentType === 'receipt_tax_invoice';
  }

  get isCreditNote() {
    return this.documentType === 'credit_note';
  }

  get isDebitNote() {
    return this.documentType === 'debit_note';
  }

  get isDeliveryOrder() {
    return this.documentType === 'delivery_order_tax_invoice';
  }

  createDocumentForm() {
    const documentType = this.documentTypes.length === 1 ? this.documentTypes[0].value : null;
    const required = (this.isCreditNote || this.isDebitNote) ? Validators.required : null;

    this.documentForm = this.formBuilder.group({
      documentNo: [null, Validators.required],
      documentDate: [this.today, Validators.required],
      abbNo: [],
      confirmationNo: [{ value: null, disabled: true }],
      documentRequest: [],
      orderNo: [{ value: null, disabled: true }],
      runningNo: [{ value: null, disabled: true }],
      purpose: [null],
      department: this.isReceiptTaxInvoice ? [null, Validators.required] : [null],
      referenceDocuments: this.formBuilder.array([
        this.formBuilder.group({
          documentNo: [null],
          documentType: [documentType],
          documentDate: [null]
        }, { asyncValidator: this.refDocumentValidator() })
      ])
    });
    if (this.documentInformation && this.documentInformation.header) {
      this.getRunnningInvoiceNo(this.documentInformation.header.department);
    }
  }

  refDocumentValidator(): AsyncValidatorFn {
    return (control: FormGroup): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> => {
      let documentNoValue = control.get('documentNo') ? control.get('documentNo').value : null;
      let documentType = control.get('documentType') ? control.get('documentType').value : null;
      let documentDate = control.get('documentDate') ? control.get('documentDate').value : null;
      let purpose = this.documentForm ? this.documentForm.get('purpose').value : null;

      return of(null).pipe(
        switchMap(() => {
          let result = {};
          if ((documentNoValue && (documentType || this.isReceiptTaxInvoice) && documentDate && purpose) || (!documentNoValue && (!documentType || this.isReceiptTaxInvoice) && !documentDate && !purpose)) {
            result = { documentNoRequired: false,
                       documentTypeRequired: false,
                       documentDateRequired: false};
            this.purposeError = false;
            return of(null)
          } else {
            result = {documentNoRequired: !documentNoValue,
                      documentTypeRequired: !documentType,
                      documentDateRequired: !documentDate};
            this.purposeError = !purpose;
          }
          return of(result);
        })
      );
    };
  }

  get documentNo() {
    return this.documentForm.get('documentNo');
  }

  get documentDate() {
    return this.documentForm.get('documentDate');
  }

  get abbNo() {
    return this.documentForm.get('abbNo');
  }

  get confirmationNo() {
    return this.documentForm.get('confirmationNo');
  }

  get orderNo() {
    return this.documentForm.get('orderNo');
  }

  get runningNo() {
    return this.documentForm.get('runningNo');
  }

  get purpose() {
    return this.documentForm.get('purpose');
  }

  get referenceDocuments() {
    return this.documentForm.get('referenceDocuments') as FormArray;
  }

  get department() {
    return this.documentForm.get('department');
  }

  getReferenceDocument(index: number) {
    return this.referenceDocuments.controls[index]
  }

  getReferenceDocumentNo(index: number) {
    return this.referenceDocuments.controls[index].get('documentNo');
  }

  getReferenceDocumentType(index: number) {
    return this.referenceDocuments.controls[index].get('documentType');
  }

  getReferenceDocumentDate(index: number) {
    return this.referenceDocuments.controls[index].get('documentDate');
  }

  addReferenceDocument() {
    this.referenceDocuments.push(
      this.formBuilder.group({
        documentNo: [null, Validators.required],
        documentType: [null, Validators.required],
        documentDate: [null, Validators.required]
      })
    );
  }

  removeReferenceDocument(index: number) {
    this.referenceDocuments.removeAt(index);
  }

  getRunnningInvoiceNo(id: number): void {
    if (!id) {
      return
    }

    const params = {
      department_id: id,
    };
    this.apiService.getInvoiceNumber(params).subscribe(
      (res) => {
        if (res) {
          this.documentForm.controls.documentNo.setValue(res)
        }
      },
      (error) => {
        console.log(error);
      }
    );
  }
}



// WEBPACK FOOTER //
// /opt/app/src/app/features/document/document-issue/components/document-form/document-form.component.ts